import { DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { MatIcon } from '@angular/material/icon'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { MatProgressBar } from '@angular/material/progress-bar'
import { AccountOpportunityListItemDto } from '../../../api/generated/api.generated'
import { ButtonComponent } from '../../../shared/button/button.component'
import { AccountOpportunitySortColumn, SortDirection } from '../../model/program-initiation.model'

interface ListColumn {
  id: AccountOpportunitySortColumn
  label: string
  /** Fixed width; columns without one share the remaining space */
  width?: string
}

export interface ProgramInitiationSortChange {
  sortBy: AccountOpportunitySortColumn
  sortDirection: SortDirection
}

export interface ProgramInitiationPageChange {
  /** 1-based */
  page: number
  pageSize: number
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-program-initiation-list',
  templateUrl: './program-initiation-list.component.html',
  styleUrls: ['./program-initiation-list.component.scss'],
  imports: [ButtonComponent, DatePipe, MatIcon, MatPaginator, MatProgressBar],
})
export class ProgramInitiationListComponent {
  @Input() rows: AccountOpportunityListItemDto[] | null
  @Input() totalCount = 0
  @Input() page = 1
  @Input() pageSize = 50
  @Input() sortBy: AccountOpportunitySortColumn
  @Input() sortDirection: SortDirection
  @Input() loading = false
  @Input() isSaving: boolean | null
  /** Row whose initiation is in progress, shown as "Processing..." while isSaving */
  @Input() initiatingOppId: string | null
  @Input() emptyMessage = 'No opportunities found.'

  @Output() initiate = new EventEmitter<AccountOpportunityListItemDto>()
  @Output() sortChange = new EventEmitter<ProgramInitiationSortChange>()
  @Output() pageChange = new EventEmitter<ProgramInitiationPageChange>()

  readonly pageSizeOptions = [25, 50, 100, 200]

  readonly columns: ListColumn[] = [
    { id: 'accountName', label: 'Account' },
    { id: 'opportunityName', label: 'Opportunity Name' },
    { id: 'opportunityStage', label: 'Stage', width: '7rem' },
    { id: 'opportunityBU', label: 'Business Unit', width: '9rem' },
    { id: 'opportunityClass', label: 'Class', width: '8rem' },
    { id: 'opportunitySubClass', label: 'Sub-Class', width: '8rem' },
    { id: 'opportunityRiskType', label: 'Risk Type', width: '6rem' },
    { id: 'opportunityInceptionDate', label: 'Inception', width: '6.5rem' },
    { id: 'initiatedDate', label: 'Initiated', width: '7rem' },
  ]

  onSort(column: AccountOpportunitySortColumn): void {
    const sortDirection = this.sortBy === column && this.sortDirection === 'asc' ? 'desc' : 'asc'
    this.sortChange.emit({ sortBy: column, sortDirection })
  }

  onPage(event: PageEvent): void {
    this.pageChange.emit({ page: event.pageIndex + 1, pageSize: event.pageSize })
  }
}
